import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { MarketButton, MarketLink } from '@market/react';
import CircleAdd from '@square-icons/react/24/Circle/circle-plus';
import TransformingQuantitySelector from './transforming_quantity_selector';
import { updateItem } from 'utilities/cart';
import { shopImageSrcSet } from 'utilities/images';
import { localizedShopPath } from 'utilities/locales';
import { updateCartCount } from 'utilities/nav';
import { useDebouncedCallback } from 'use-debounce';
import { trackAddToCart } from 'components/pages/product/helpers';

const useStyles = makeStyles((theme) => ({
  card: {
    position: 'relative',
    backgroundColor: theme.color.gray['5'],
    borderRadius: '5px',
    border: 'none',
    width: '100%',
    minHeight: '140px',
  },
  imgContainer: {
    height: '122px',
    width: '122px',
    flex: '0 0 auto',
    alignSelf: 'center',
  },
  img: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
  },
  nonImageInfo: {
    margin: '24px 24px 24px 8px',
    width: '100%',
  },
  selector: {
    maxWidth: '120px',
    height: '36px',
    position: 'relative',
  },
}));

const UpsellCard = (props) => {
  const classes = useStyles();
  const {
    upsell,
    quantity,
    cart,
    updateQuantity,
    setToast,
    setSubtotal,
    setItemCount,
    setAccessoryModal,
    trackEvent,
    trackingEventTypes,
  } = props;

  const productName = upsell.name;
  const price = upsell.prices.sale_price;

  const { t } = useTranslation();
  const debouncedQuantity = useRef(0);
  const [active, setActive] = useState(false);
  const theme = useTheme();

  const onUpdateCartSuccess = (response, delta) => {
    // Show toast if quantity is changing from the initial 0 value
    if (debouncedQuantity.current === 0 && delta > 0) {
      setToast({
        timeout: 3000,
        open: true,
        message: t('productPage.addedToCart', { name: upsell.name }),
        variant: 'success',
        action: (
          <MarketLink href={`${localizedShopPath()}/cart`}>
            {t('productPage.viewCart')}
          </MarketLink>
        ),
      });
    }
    // Only track quantity increases
    if (delta > 0) {
      trackAddToCart({
        mainProductTrackingData: upsell.tracking_data,
        cartNumber: cart.number,
        quantity: delta,
      });
    }

    updateCartCount(response.item_count);
    setItemCount(response.item_count);
    setSubtotal(response.subtotal);
    debouncedQuantity.current = quantity;
  };

  const debounced = useDebouncedCallback((value) => {
    const delta = value - debouncedQuantity.current;
    if (delta === 0) {
      return;
    }

    const action = delta > 0 ? 'add' : 'subtract';
    updateItem(
      upsell.variant_token,
      Math.abs(delta),
      [],
      (response) => onUpdateCartSuccess(response, delta),
      null, // onError
      action,
    );
  }, 500);

  const onQuantityChange = (value) => {
    if (value === 0) {
      setActive(false);
    }
    updateQuantity(upsell.slug, value);
    debounced.callback(value);
  };

  const onActivateSelector = () => {
    trackEvent({
      eventName: trackingEventTypes.AddToCart,
      params: {
        product: { ...upsell.tracking_data },
      },
    });
    setActive(true);
    if (quantity === 0) {
      onQuantityChange(1);
    }
  };

  const onViewButtonClick = () => {
    trackEvent({
      eventName: trackingEventTypes.ModalViewButtonClicked,
      params: {
        product: { ...upsell.tracking_data },
      },
    });

    setAccessoryModal({
      open: true,
      name: upsell.name,
      displayPrice: upsell.sale_price,
      description: upsell.description,
      imgSrc: upsell.images.tile,
      slug: upsell.slug,
      variantToken: upsell.variant_token,
      trackingData: upsell.tracking_data,
      handleClose: () => setAccessoryModal({ open: false }),
      cart,
    });
  };

  return (
    <Grid container className={classes.card} wrap="nowrap">
      <Grid item className={classes.imgContainer}>
        <img
          className={classes.img}
          srcSet={shopImageSrcSet({
            baseUrl: upsell.images.tile,
            type: 'productTile',
            fm: 'png',
          })}
        />
      </Grid>
      <Grid container item direction="column" className={classes.nonImageInfo}>
        <Box mb={2} flexGrow={1}>
          <p className="medium-20">{productName}</p>
          <p className="paragraph-20">{price}</p>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <MarketButton
            onClick={onViewButtonClick}
            rank="tertiary"
            style={{
              backgroundColor: 'transparent',
              color: theme.color.blue,
            }}
          >
            <CircleAdd slot="icon" />
            {t('productPage.view')}
          </MarketButton>
          <div className={classes.selector}>
            <TransformingQuantitySelector
              value={quantity}
              active={active}
              onValueChange={onQuantityChange}
              onButtonClick={onActivateSelector}
              onOutsideClick={() => setActive(false)}
              ariaQuantityName={productName}
            />
          </div>
        </Box>
      </Grid>
    </Grid>
  );
};

export default UpsellCard;
